import sanitizeHtml from 'sanitize-html';
import { decode } from 'html-entities';

import {
  maybeInterceptExternalLinkForAndroidIntent,
} from '@utils/androidBrowserIntentUrl.ts';
import { ITextOptions } from '../../utils/interfaces.ts';
import React from 'react';

const SANITIZE_HTML_OPTIONS = {
  allowedClasses: {
    '*': ['*'],
  },
  allowedSchemes: ['http', 'https', 'mailto'],
  allowedSchemesByTag: {
    a: ['http', 'https', 'mailto'],
  },
} as const;

const hrefForAnchor = (href: string) => {
  return href.trim();
};

export const postfixAndTrim = (text: string, start: number) => {
  return text.substring(start).trim();
};


export const processBlockToJsx = (text: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  let currentTextAccumulator = '';
  let anchorTextContent = '';
  let anchorHrefContent = '';
  let tempTagContent = ''; // For content inside <b> or <i>
  let state = 'normal';
  const len = text.length;
  let nodeKeyIndex = 0; // Counter for generating unique keys

  const flushCurrentText = () => {
    if (currentTextAccumulator) {
      // Plain strings in an array don't need keys themselves,
      // but if they were wrapped in a <span key={...}> for some reason, they would.
      // Here, we just push the string.
      nodes.push(currentTextAccumulator);
      currentTextAccumulator = '';
    }
  };

  for (let i = 0; i < len; i++) {
    const symbol = text[i];

    switch (state) {
      case 'normal':
        if (symbol === '^') {
          flushCurrentText();
          state = 'sawCaret';
        } else {
          currentTextAccumulator += symbol;
        }
        break;
      case 'sawCaret':
        if (symbol === '*') {
          tempTagContent = '';
          state = 'inBold';
        } else if (symbol === '&') {
          tempTagContent = '';
          state = 'inItalic';
        } else if (symbol === '[') {
          anchorTextContent = '';
          anchorHrefContent = '';
          tempTagContent = '';
          state = 'inAnchorText';
        } else {
          currentTextAccumulator += '^' + symbol;
          state = 'normal';
        }
        break;
      case 'inBold':
        if (symbol === '^') {
          state = 'inBoldSawCaret';
        } else {
          tempTagContent += symbol;
        }
        break;
      case 'inBoldSawCaret':
        if (symbol === '*') {
          nodes.push(<b key={`bold-${nodeKeyIndex++}`}>{tempTagContent}</b>);
          tempTagContent = '';
          state = 'normal';
        } else {
          tempTagContent += '^' + symbol;
          state = 'inBold';
        }
        break;
      case 'inItalic':
        if (symbol === '^') {
          state = 'inItalicSawCaret';
        } else {
          tempTagContent += symbol;
        }
        break;
      case 'inItalicSawCaret':
        if (symbol === '&') {
          nodes.push(<i key={`italic-${nodeKeyIndex++}`}>{tempTagContent}</i>);
          tempTagContent = '';
          state = 'normal';
        } else {
          tempTagContent += '^' + symbol;
          state = 'inItalic';
        }
        break;
      case 'inAnchorText':
        if (symbol === '^') {
          state = 'inAnchorTextSawCaret';
        } else {
          tempTagContent += symbol;
        }
        break;
      case 'inAnchorTextSawCaret':
        if (symbol === '|') {
          anchorTextContent = tempTagContent;
          tempTagContent = '';
          state = 'inAnchorHref';
        } else {
          tempTagContent += '^' + symbol;
          state = 'inAnchorText';
        }
        break;
      case 'inAnchorHref':
        if (symbol === '^') {
          state = 'inAnchorHrefSawCaret';
        } else {
          tempTagContent += symbol;
        }
        break;
      case 'inAnchorHrefSawCaret':
        if (symbol === ']') {
          anchorHrefContent = tempTagContent;
          const hrefRaw = hrefForAnchor(anchorHrefContent);
          nodes.push(
            <a
              key={`link-${nodeKeyIndex++}`}
              className="inline-link"
              href={hrefRaw}
              onClick={(e) => {
                maybeInterceptExternalLinkForAndroidIntent(e, hrefRaw);
              }}
            >
              {anchorTextContent}
            </a>
          );
          tempTagContent = '';
          anchorTextContent = '';
          anchorHrefContent = '';
          state = 'normal';
        } else {
          tempTagContent += '^' + symbol;
          state = 'inAnchorHref';
        }
        break;
      default: {
        currentTextAccumulator += symbol;
      }
    }
  }

  flushCurrentText();

  if (tempTagContent) {
    // If an unclosed tag's content remains, push it as plain text.
    // React will escape it.
    nodes.push(tempTagContent);
  }

  return nodes;
};

export const processBlockToString = (text: string) => {
  let result = '';
  let anchorText = '';
  let anchorHRef = '';
  let resultOffset = 0;
  let state = 'normal';
  const len = text.length;

  for (let i = 0; i < len; i++) {
    const symbol = text[i];

    switch (state) {
      case 'normal':
        if (symbol === '^') {
          state = 'sawCaret';
        } else {
          result += symbol;
        }
        break;
      case 'sawCaret':
        if (symbol === '*') {
          result += '<b>';
          state = 'inCaret';
        } else if (symbol === '&') {
          result += '<i>';
          state = 'inCaret';
        } else if (symbol === '[') {
          anchorText = '';
          anchorHRef = '';
          resultOffset = result.length; // Start location for text.
          state = 'anchorText';
        } else {
          result += symbol;
          state = 'normal';
        }
        break;
      case 'inCaret':
        if (symbol === '^') {
          state = 'inCaretSawCaret';
        } else {
          result += symbol;
        }
        break;
      case 'inCaretSawCaret':
        if (symbol === '*') {
          result += '</b>';
          state = 'normal';
        } else if (symbol === '&') {
          result += '</i>';
          state = 'normal';
        } else {
          result += symbol;
          state = 'inCaret';
        }
        break;
      case 'anchorText':
        if (symbol === '^') {
          state = 'anchorTextSawCaret';
        } else {
          result += symbol;
        }
        break;
      case 'anchorTextSawCaret':
        if (symbol === '|') {
          anchorText = result.substring(resultOffset, result.length);
          result = result.substring(0, resultOffset);
          state = 'anchorBody';
        } else {
          result += '^' + symbol;
          state = 'anchorText';
        }
        break;
      case 'anchorBody':
        if (symbol === '^') {
          state = 'anchorBodySawCaret';
        } else {
          result += symbol;
        }
        break;
      case 'anchorBodySawCaret':
        if (symbol === ']') {
          anchorHRef = result.substring(resultOffset, result.length);
          result = result.substring(0, resultOffset);
          result +=
            '<a class="inline-link" href="' +
            hrefForAnchor(anchorHRef) +
            '">' +
            anchorText +
            '</a>';
          state = 'normal';
        } else {
          result += '^' + symbol;
          state = 'anchorBody';
        }
        break;
      default: {
        result += symbol;
      }
    }
  }
  return sanitizeHtml(result, SANITIZE_HTML_OPTIONS);
};

export const processBlock = (block: string, option?: ITextOptions) => {
  if (option?.useJsx) {
    return processBlockToJsx(block)
  } else {
    return processBlockToString(block)
  }
}

export const getCleanText = (text: string) => {
  return text.trim().replace(/\s{2,}/g, ' ');
};

export const getMarkdown = (text: string, options?: ITextOptions): React.ReactNode[] => {
  const lines = text.split('\n');
  const componentList: React.ReactNode[] = [];

  for (let i = 0; i < lines.length; i++) {
    const cleanText = getCleanText(lines[i]);

    if (cleanText.startsWith('# ')) {
      const content = processBlock(postfixAndTrim(cleanText, 2), options);
      componentList.push(
        <React.Fragment key={`h1-frag-${i}`}>
          <h1
            className="headline--s"
            key={`h1-${i}`}
            {...(options?.useJsx ? {} : { dangerouslySetInnerHTML: { __html: content as string } })}
          >
            {options?.useJsx ? content : null}
          </h1>
          <hr key={`hr-after-h1-${i}`} />
        </React.Fragment>
      );
    } else if (cleanText.startsWith('## ')) {
      const className = options?.subHeaderFirstLevelClass
        ? options.subHeaderFirstLevelClass
        : 'headline--m';
      const content = processBlock(postfixAndTrim(cleanText, 3), options);

      componentList.push(
        <h2
          className={className}
          key={`h2-${i}`}
          {...(options?.useJsx ? {} : { dangerouslySetInnerHTML: { __html: content as string } })}
        >
          {options?.useJsx ? content : null}
        </h2>
      );
    } else if (cleanText.startsWith('### ')) {
      const content = processBlock(postfixAndTrim(cleanText, 4), options);
      componentList.push(
        <React.Fragment key={`h3-frag-${i}`}>
          <h1 // Per your original code, ### maps to h1. Consider if h3 is more semantic.
            className="headline--s"
            key={`h3-${i}`} // Using h3 in key for clarity if you change the tag
            {...(options?.useJsx ? {} : { dangerouslySetInnerHTML: { __html: content as string } })}
          >
            {options?.useJsx ? content : null}
          </h1>
          <hr key={`hr-after-h3-${i}`} />
        </React.Fragment>
      );
    } else if (cleanText.startsWith('#### ')) {
      const content = processBlock(postfixAndTrim(cleanText, 5), options);
      componentList.push(
        <h1 // Per your original code, #### maps to h1. Consider if h4 is more semantic.
          key={`h4-${i}`} // Using h4 in key for clarity
          className="headline--m"
          {...(options?.useJsx ? {} : { dangerouslySetInnerHTML: { __html: content as string } })}
        >
          {options?.useJsx ? content : null}
        </h1>
      );
    } else if (cleanText.startsWith('@credit ')) {
      const content = processBlock(postfixAndTrim(cleanText, 8), options);
      componentList.push(
        <p
          key={`credit-${i}`}
          className="credit"
          {...(options?.useJsx ? {} : { dangerouslySetInnerHTML: { __html: content as string } })}
        >
          {options?.useJsx ? content : null}
        </p>
      );
    } else if (cleanText.startsWith('@hr')) {
      componentList.push(<hr key={`hr-${i}`} />);
    } else if (cleanText.startsWith('@image(')) {
      const [tag, src, width, height] =
      cleanText.match(/^@image\(([^,]+),([^,]+),([^)]+)\)$/) || [];

      if (tag && src && width && height) { // Added checks for all parts
        componentList.push(
          <img
            alt="content"
            key={`image-${src}-${i}`} // Made key more unique
            src={src}
            width={width}
            height={height}
            className="image"
          />
        );
      }
    } else if (cleanText.startsWith('@link(')) {
      const found2 = cleanText.match(/^@link\(([^)]+)\)\s+(.+)$/);
      const link = found2?.length && found2[1] ? found2[1] : '';
      const linkText = found2?.length && found2[2] ? found2[2] : ''; // Ensure linkText is captured
      if (link && linkText && !options?.isIgnoreLinks) { // Added check for linkText
        const hrefTrimmed = hrefForAnchor(link);
        componentList.push(
          <a
            href={hrefTrimmed}
            className="source_link"
            key={`link-${i}`}
            onClick={(e) => {
              maybeInterceptExternalLinkForAndroidIntent(e, hrefTrimmed);
            }}
          >
            {linkText}
          </a>
        );
      }
    } else if (cleanText.startsWith('@cta(')) {
      const [tag, href, ctaText] = // Renamed 'text' to 'ctaText'
      cleanText.match(/^@cta\(([^)]+)\)\s+(.+)$/) || [];

      if (tag && href && ctaText) { // Added checks for all parts
        const hrefTrimmed = hrefForAnchor(href);
        componentList.push(
          <a
            href={hrefTrimmed}
            className="button"
            key={`cta-${i}`}
            onClick={(e) => {
              maybeInterceptExternalLinkForAndroidIntent(e, hrefTrimmed);
            }}
          >
            {ctaText}
          </a>
        );
      }
    } else if (cleanText.startsWith('@sp')) {
      componentList.push(<p key={`sp-${i}`}>&nbsp;</p>);
    } else if (cleanText.startsWith('@ad')) {
      const mackResult = cleanText.match(/^@ad\(([^)]+)\)/) || [];
      const slotID = mackResult?.length && mackResult[1] ? mackResult[1] : ''; // Ensure slotID is captured

      if (slotID) {
        componentList.push(
          <div className="ad-wrapper" key={`ad-${slotID}-${i}`}> {/* Made key more unique */}
            <div id={`ad_unit_${slotID}`}></div>
          </div>
        );
      }
    } else if (cleanText && !options?.isIgnoreParagraph) { // Added check for cleanText to avoid empty <p>
      const content = processBlock(postfixAndTrim(cleanText, 0), options);
      componentList.push(
        <p
          key={`p-${i}`}
          className={
            options?.paragraphClass ? options.paragraphClass : 'paragraph--s'
          }
          {...(options?.useJsx ? {} : { dangerouslySetInnerHTML: { __html: content as string } })}
        >
          {options?.useJsx ? content : null}
        </p>
      );
    } else if (cleanText) { // If not a special line and not an ignored paragraph, but still has text
      // This will push React.ReactNode[] or string directly into the list.
      // If it's React.ReactNode[], React will render it as a sequence of elements/strings.
      // If it's a string, React will render it as a text node.
      componentList.push(processBlock(postfixAndTrim(cleanText, 0), options));
    }
  }

  return componentList;
};

export const stripHTML = (str: string) => {
  const stripped = str?.replace(/<[^>]*>/g, "") || '';
  return decode(stripped);
};
